import { createContext, type ReactNode, useContext, useEffect, useRef, useState } from 'react'

type NewlyCreatedRecord = { collection: string; id: string } | null

interface NewlyCreatedRecordContextType {
    record: NewlyCreatedRecord
    setRecord: (record: NewlyCreatedRecord) => void
}

const NewlyCreatedRecordContext = createContext<NewlyCreatedRecordContextType | null>(null)

export function NewlyCreatedRecordProvider({ children }: { children: ReactNode }) {
    const [record, setRecord] = useState<NewlyCreatedRecord>(null)
    return (
        <NewlyCreatedRecordContext.Provider value={{ record, setRecord }}>
            {children}
        </NewlyCreatedRecordContext.Provider>
    )
}

function useCtx() {
    const ctx = useContext(NewlyCreatedRecordContext)
    if (!ctx) {
        throw new Error('NewlyCreatedRecord hooks must be used within a NewlyCreatedRecordProvider')
    }
    return ctx
}

export function useReportNewlyCreated() {
    const { setRecord } = useCtx()
    return (collection: string, id: string) => {
        setRecord({ collection, id })
    }
}

export function useConsumeNewlyCreated(collection: string, onCreated: (id: string) => void) {
    const { record, setRecord } = useCtx()
    const onCreatedRef = useRef(onCreated)
    useEffect(() => {
        onCreatedRef.current = onCreated
    })
    useEffect(() => {
        if (record && record.collection === collection) {
            const id = record.id
            setRecord(null)
            onCreatedRef.current(id)
        }
    }, [record, collection, setRecord])
}
